import { useCallback, useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Boxes, Plus, ArrowLeftRight, Loader2, AlertTriangle, Pencil, History, Repeat } from "lucide-react";
import { PageHead, Stat, Empty, inputCls, btnPrimary, btnGhost, qtyFmt } from "@/components/erp/ErpUi";
import { ProductDialog, MoveDialog, HistoryDialog, TransferDialog } from "@/components/erp/StockDialogs";

export default function OwnerStock() {
  const { user } = useAuth();
  const scopeAll = user?.shop_id === "all";
  const [q, setQ] = useState("");
  const [low, setLow] = useState(false);
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState({ kind: null, product: null });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/erp/products", { params: { q, low } }); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [q, low]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const open = (kind, product = null) => setDlg({ kind, product });
  const close = () => setDlg({ kind: null, product: null });
  const multi = (user?.shops || []).length > 1;

  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={Boxes} kicker="Estoque" title="Produtos e peças">
        {multi && !scopeAll && <Button data-testid="stock-transfer-btn" variant="outline" className={btnGhost} onClick={() => open("transfer")}><ArrowLeftRight className="h-4 w-4 mr-1" /> Transferir</Button>}
        <Button data-testid="stock-new-btn" disabled={scopeAll} className={btnPrimary} onClick={() => open("product")}><Plus className="h-4 w-4 mr-1" /> Novo produto</Button>
      </PageHead>
      {scopeAll && <p data-testid="stock-scope-hint" className="text-xs text-amber-300">Visão consolidada de todas as unidades. Para cadastrar ou transferir, escolha uma unidade no cabeçalho.</p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="stock-count" label="Itens" value={data?.summary.count ?? "…"} />
        <Stat testid="stock-value" label="Valor em estoque (custo)" value={data ? brl(data.summary.stock_value) : "…"} tone="green" />
        <Stat testid="stock-low" label="Abaixo do mínimo" value={data?.summary.low_count ?? "…"} tone="amber" />
        <Stat testid="stock-negative" label="Negativos" value={data?.summary.negative_count ?? "…"} tone="red" />
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por descrição, código, código de barras ou marca" className={inputCls} />
        <Button data-testid="stock-low-toggle" variant="outline" onClick={() => setLow((v) => !v)} className={low ? "bg-amber-brand text-black font-bold hover:bg-amber-brandDark" : btnGhost}>
          <AlertTriangle className="h-4 w-4 mr-1" /> Só abaixo do mínimo
        </Button>
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : data.items.length === 0 ? <Empty>Nenhum produto cadastrado.</Empty> : (
        <div className="rounded-2xl border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm min-w-[940px]">
            <thead><tr className="bg-zinc-900 text-[11px] font-mono-caps text-zinc-500 uppercase">
              <th className="text-left px-3 py-2">Código</th><th className="text-left px-3 py-2">Descrição</th>
              {scopeAll && <th className="text-left px-3 py-2">Unidade</th>}
              <th className="text-left px-3 py-2">Local</th><th className="text-right px-3 py-2">Estoque</th><th className="text-right px-3 py-2">Mín.</th>
              <th className="text-right px-3 py-2">Custo</th><th className="text-right px-3 py-2">Venda</th><th className="text-right px-3 py-2">Margem</th><th className="text-right px-3 py-2">Markup</th><th className="px-3 py-2" />
            </tr></thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} data-testid={`stock-row-${p.id}`} className="border-t border-zinc-800">
                  <td className="px-3 py-2 font-mono text-xs text-zinc-400">{p.code}</td>
                  <td className="px-3 py-2 text-zinc-100">{p.description}<div className="text-[11px] text-zinc-500">{[p.brand, p.barcode].filter(Boolean).join(" · ")}</div></td>
                  {scopeAll && <td className="px-3 py-2 text-xs text-zinc-400">{p.shop_name}</td>}
                  <td className="px-3 py-2 text-xs text-zinc-400">{p.location || "—"}</td>
                  <td className={`px-3 py-2 text-right font-bold ${p.negative ? "text-red-300" : p.low ? "text-amber-brand" : "text-zinc-100"}`}>
                    {qtyFmt(p.stock)} {p.unit}{(p.low || p.negative) && <AlertTriangle data-testid={`stock-alert-${p.id}`} className="inline h-3.5 w-3.5 ml-1" />}
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-400">{qtyFmt(p.min_stock)}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{brl(p.cost)}</td>
                  <td className="px-3 py-2 text-right text-zinc-100">{brl(p.price)}</td>
                  <td className={`px-3 py-2 text-right ${p.margin < 0 ? "text-red-300" : "text-green-neon"}`}>{p.margin.toFixed(1)}%</td>
                  <td data-testid={`stock-markup-${p.id}`} className={`px-3 py-2 text-right ${p.markup < 0 ? "text-red-300" : "text-amber-brand"}`}>{p.markup == null ? "—" : `${p.markup.toFixed(1)}%`}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <button data-testid={`stock-move-${p.id}`} title="Movimentar" onClick={() => open("move", p)} className="p-1.5 text-zinc-400 hover:text-green-neon"><Repeat className="h-4 w-4" /></button>
                    <button data-testid={`stock-history-${p.id}`} title="Histórico" onClick={() => open("history", p)} className="p-1.5 text-zinc-400 hover:text-green-neon"><History className="h-4 w-4" /></button>
                    <button data-testid={`stock-edit-${p.id}`} title="Editar" onClick={() => open("product", p)} className="p-1.5 text-zinc-400 hover:text-green-neon"><Pencil className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ProductDialog open={dlg.kind === "product"} onOpenChange={(o) => !o && close()} product={dlg.product} onSaved={load} />
      <MoveDialog open={dlg.kind === "move"} onOpenChange={(o) => !o && close()} product={dlg.product} onSaved={load} />
      <HistoryDialog open={dlg.kind === "history"} onOpenChange={(o) => !o && close()} product={dlg.product} />
      <TransferDialog open={dlg.kind === "transfer"} onOpenChange={(o) => !o && close()} products={data?.items || []} shops={user?.shops} fromShopId={user?.shop_id} onSaved={load} />
    </div>
  );
}
